import { Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CropperPosition, ImageCropperComponent, LoadedImage } from 'ngx-image-cropper';

import { MessageService } from '@services/message.service';
import { RecorteFoto } from '../../services/fotos-vistoria.service';

/** API de modais do navegador (Chrome/Edge 120+), ainda fora do lib.dom do TypeScript do projeto. */
declare class CloseWatcher {
  onclose: (() => void) | null;
  destroy(): void;
}

export interface DadosDialogRecorteFoto {
  arquivo: File;
  posicao: number;
  total: number;
}

/**
 * Recorte quadrado obrigatório da foto, em tela cheia. Devolve o recorte em pixels da foto (já com a
 * orientação do EXIF aplicada), ou null se cancelado: o corte e a redução são feitos depois, num passo só,
 * pelo `FotosVistoriaService` — o cropper não gera imagem nenhuma.
 *
 * O Voltar do Android e o Esc do computador cancelam o recorte pela `CloseWatcher`, sem entrada no histórico
 * (o Router não fica sabendo). Navegador sem ela (Safari, Firefox): só os botões; o gesto de voltar sai da tela.
 */
@Component({
  selector: 'app-dialog-recorte-foto',
  standalone: false,
  templateUrl: './dialog-recorte-foto.component.html',
  styleUrl: './dialog-recorte-foto.component.scss'
})
export class DialogRecorteFotoComponent implements OnInit, OnDestroy {

  @ViewChild(ImageCropperComponent) cropper!: ImageCropperComponent;

  posicaoRecorte?: CropperPosition;
  pronto = false;
  private larguraFoto = 0;
  private closeWatcher?: CloseWatcher;
  private imagemCarregada?: LoadedImage;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dados: DadosDialogRecorteFoto,
    private dialogRef: MatDialogRef<DialogRecorteFotoComponent, RecorteFoto | null>,
    private messageService: MessageService,
    private ngZone: NgZone,
  ) { }

  ngOnInit(): void {
    if ('CloseWatcher' in window) {
      this.closeWatcher = new CloseWatcher();
      // O zone.js não conhece a CloseWatcher: sem o run, a tela não seria atualizada ao fechar.
      this.closeWatcher.onclose = () => this.ngZone.run(() => this.cancelar());
    }
  }

  /** O cropper cria URLs pra cópia da foto (e pra versão com a orientação aplicada) e nunca as revoga: sem isto,
   * cada recorte deixaria a foto inteira presa na memória até sair da tela. */
  ngOnDestroy(): void {
    this.closeWatcher?.destroy();
    if (this.imagemCarregada) {
      URL.revokeObjectURL(this.imagemCarregada.original.objectUrl);
      URL.revokeObjectURL(this.imagemCarregada.transformed.objectUrl);
    }
  }

  public onImagemCarregada(imagem: LoadedImage): void {
    this.imagemCarregada = imagem;
    this.larguraFoto = imagem.transformed.size.width;
  }

  public onFalhaAoCarregar(): void {
    this.messageService.showToastWarning(`Não foi possível ler a foto "${this.dados.arquivo.name}"`);
    this.dialogRef.close(null);
  }

  /** O quadro do cropper está em pixels da imagem exibida; a escala converte pra pixels da foto. */
  public confirmar(): void {
    const imagemExibida = this.cropper.sourceImage.nativeElement;
    const escala = this.larguraFoto / parseFloat(getComputedStyle(imagemExibida).width);
    const posicao = this.posicaoRecorte!;

    this.dialogRef.close({
      x: Math.round(posicao.x1 * escala),
      y: Math.round(posicao.y1 * escala),
      lado: Math.round((posicao.x2 - posicao.x1) * escala),
    });
  }

  public cancelar(): void {
    this.dialogRef.close(null);
  }
}
